import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth, type User } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getFunctions, type Functions } from "firebase/functions";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from "firebase/app-check";
import { env } from "./env";

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _functions: Functions | null = null;
let _appCheck: AppCheck | null = null;

export function getFirebase() {
  if (_app) return { app: _app, auth: _auth!, db: _db!, functions: _functions! };

  _app = initializeApp({
    apiKey: env.firebase.apiKey,
    authDomain: env.firebase.authDomain,
    projectId: env.firebase.projectId,
    appId: env.firebase.appId,
    messagingSenderId: env.firebase.messagingSenderId,
    storageBucket: env.firebase.storageBucket,
  });

  if (env.recaptchaSiteKey) {
    // ReCaptchaEnterpriseProvider: the site key is an Enterprise key — a v3 provider mints tokens the exchange rejects (see docs/TechArchitecture.md, App Check).
    _appCheck = initializeAppCheck(_app, {
      provider: new ReCaptchaEnterpriseProvider(env.recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  _auth = getAuth(_app);
  _db = getFirestore(_app);
  _functions = getFunctions(_app, env.functionsRegion);

  return { app: _app, auth: _auth, db: _db, functions: _functions };
}

/**
 * The signed-in user once Firebase has restored persistence. Route
 * `beforeLoad` runs before the auth listener has fired on a hard reload, when
 * `auth.currentUser` is null for everyone; waiting for the first state event
 * distinguishes "signed out" from "not loaded yet".
 */
export function authSettled(auth: Auth): Promise<User | null> {
  if (auth.currentUser !== null) return Promise.resolve(auth.currentUser);
  return new Promise((resolve) => {
    const unsub = auth.onAuthStateChanged((u) => {
      unsub();
      resolve(u);
    });
  });
}

export function getAppCheck() {
  return _appCheck;
}
